import React, { useEffect, useState } from "react";
import { Modal, Button, Form, message, Spin } from "antd";
import ReadingForm from "./Form";
import {
  useReadingCreate,
  useReadingUpdate,
  useReadingGetById,
} from "../../../../../hooks/api/readings";

interface ReadingModalProps {
  open: boolean;
  mode: "create" | "edit";
  onClose: () => void;
  onSave?: () => void;
  recordId?: string;
}

export default function ReadingModal({
  open,
  mode,
  onClose,
  onSave,
  recordId,
}: ReadingModalProps): React.ReactElement {
  const [form] = Form.useForm();
  const createReading = useReadingCreate();
  const updateReading = useReadingUpdate(recordId);
  const readingById = useReadingGetById();

  const [tagOptions, setTagOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  useEffect(() => {
    if (mode === "edit" && recordId && open) {
      readingById.fetch(undefined, recordId);
    }
  }, [mode, recordId, open]);

  useEffect(() => {
    if (readingById.data?.middahs) {
      const options = readingById.data.middahs.map((m: any) => ({
        label: m.name,
        value: m.id,
      }));
      setTagOptions(options);
    }
  }, [readingById.data]);

  // Заполняем форму
  useEffect(() => {
    if (open && mode === "edit" && readingById.data) {
      const record = readingById.data;
      form.setFieldsValue({
        name: record.name,
        middahs: record.middahs?.map((m: any) => m.id) || [],
        globalContent: record.globalContent ?? false,
        type: record.type,
        fileLink: record.fileLink,
      });
    }
  }, [open, mode, readingById.data, form]);

  // Сброс при создании
  useEffect(() => {
    if (open && mode === "create") {
      form.resetFields();
      setTagOptions([]);
    }
  }, [open, mode, form]);

  const handleSubmit = async (values: any) => {
    const payload = {
      name: values.name,
      type: values.type,
      fileLink: values.fileLink,
      middahs: values.middahs?.map((id: string) => ({ id })) || [],
      globalContent: values.globalContent ?? false,
    };

    try {
      if (mode === "edit") {
        await updateReading.fetch(payload, recordId);
        message.success("Reading updated successfully");
      } else {
        await createReading.fetch(payload);
        message.success("Reading uploaded successfully");
      }
      onSave?.();
      handleClose();
    } catch (error) {
      message.error("Something went wrong");
    }
  };

  const isLoading = mode === "edit" && readingById.loading;

  const footer = [
    <Button key="cancel" onClick={handleClose}>
      Cancel
    </Button>,
    <Button
      key="save"
      type="primary"
      onClick={() => form.submit()}
      loading={
        isLoading ||
        (mode === "edit" ? updateReading.loading : createReading.loading)
      }
    >
      Save
    </Button>,
  ];

  return (
    <Modal
      title={mode === "edit" ? "Edit Reading" : "Add Reading"}
      open={open}
      onCancel={handleClose}
      width={520}
      footer={footer}
      destroyOnClose
    >
      {isLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <ReadingForm mode={mode} initialTagOptions={tagOptions} />
        </Form>
      )}
    </Modal>
  );
}
